// Entitlement and web billing.
//
// One entitlement, two purchase paths: StoreKit on iPhone and Stripe in the browser. The Worker
// reconciles both into the same row, so a subscription bought on the phone unlocks the web and vice
// versa. The browser must never try to cancel or change an Apple subscription — only the App Store
// can do that, which is why `source` drives what Settings offers.

import { apiGet, apiPost } from './client';

export interface Entitlement {
  isPremium: boolean;
  source: 'apple' | 'appstore' | 'stripe' | 'admin' | null;
  expiresAt: string | null;
  energy: number;
  maxEnergy: number;
}

interface EntitlementResponse {
  premium?: boolean;
  source?: string | null;
  expiresAt?: string | null;
  energy?: number;
  maxEnergy?: number;
}

export async function fetchEntitlement(): Promise<Entitlement> {
  const res = await apiGet<EntitlementResponse>('/api/entitlement');
  return {
    isPremium: res.premium === true,
    source: (res.source as Entitlement['source']) ?? null,
    expiresAt: res.expiresAt ?? null,
    energy: Number(res.energy ?? 0),
    maxEnergy: Number(res.maxEnergy ?? 0),
  };
}

export type PlanId = 'monthly' | 'annual';

export interface Price {
  plan: PlanId;
  /** Formatted for display in the visitor's locale, e.g. "$9.99". */
  display: string;
  /** "month" | "year" — what the amount buys. Null for a one-off price. */
  interval: string | null;
  /** Free days before the first charge. 0 when there is no trial. */
  trialDays: number;
  /** Minor units, kept so callers can compare plans without reparsing the display string. */
  amount: number;
}

interface PriceResponse {
  plans?: {
    plan?: string;
    amount?: number;
    currency?: string;
    interval?: string | null;
    trialDays?: number;
  }[];
}

function money(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    // Stripe stores minor units, so a whole-dollar price arrives as 900 and should read "$9".
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
  }).format(amount / 100);
}

/**
 * The live subscription plans, straight from Stripe.
 *
 * Returns an empty list rather than a fallback when the price can't be read: showing no price is a
 * mild disappointment, and showing a stale one is a false advertisement. The caller renders the
 * upgrade path either way, so a Stripe outage never blocks someone from subscribing.
 */
export async function fetchPrices(signal?: AbortSignal): Promise<Price[]> {
  try {
    const res = await apiGet<PriceResponse>('/api/stripe/price', signal);
    return (res.plans ?? []).flatMap((p) => {
      if (typeof p.amount !== 'number' || !p.currency) return [];
      if (p.plan !== 'monthly' && p.plan !== 'annual') return [];
      return [{
        plan: p.plan,
        display: money(p.amount, p.currency),
        interval: p.interval ?? null,
        trialDays: p.trialDays ?? 0,
        amount: p.amount,
      }];
    });
  } catch {
    return [];
  }
}

/**
 * What the yearly plan saves against twelve months of the monthly one, e.g. "Save 50%".
 * Null when either plan is missing or the yearly one is not actually cheaper.
 */
export function annualSaving(prices: Price[]): string | null {
  const monthly = prices.find((p) => p.plan === 'monthly');
  const annual = prices.find((p) => p.plan === 'annual');
  if (!monthly || !annual) return null;
  const full = monthly.amount * 12;
  if (annual.amount >= full) return null;
  return `Save ${Math.round(((full - annual.amount) / full) * 100)}%`;
}

/** Create a Stripe Checkout session and return the URL to send the browser to. */
export async function startCheckout(plan: PlanId = 'monthly'): Promise<string> {
  const res = await apiPost<{ url?: string }>('/api/stripe/checkout', { plan });
  if (!res.url) throw new Error('Checkout is unavailable right now.');
  return res.url;
}

/** Stripe's hosted billing portal, for changing or cancelling a web subscription. */
export async function openBillingPortal(): Promise<string> {
  const res = await apiPost<{ url?: string }>('/api/stripe/portal', {});
  if (!res.url) throw new Error('Billing management is unavailable right now.');
  return res.url;
}